/**
 * <ConditionalSkuRulesEditor />
 *
 * Renders the conditional Sales SKU rules for a single Visual Builder
 * option, sitting underneath the option's default Sales SKU dropdown.
 *
 * Data model (matches backend `PricingMapping.sku_rules`):
 *
 *   [
 *     {
 *       id: "r7",
 *       conditions: [
 *         { attribute: "step:format", operator: "equals", value: "7in" }
 *       ],
 *       sales_sku: "INNER-7",
 *       label: "7\" inner sleeve"   // admin-facing hint (optional)
 *     },
 *     ...
 *   ]
 *
 * v1 keeps the UI intentionally minimal:
 *  • Only "equals" operator (backend only supports exact match).
 *  • Attribute picker offers three sources: earlier flow steps
 *    (step:<step_id>), the flow's product_family, and free-form
 *    attribute keys (attribute:<field_id>).
 *  • Value picker is a string input — for step attributes we surface
 *    the target step's option ids as suggestions via a datalist so
 *    admins don't have to memorise option_ids.
 *  • Rules evaluate top-down; parent order dictates priority.
 *
 * Props:
 *   rules              — SkuRule[] currently attached to the option
 *   onChange(newRules) — parent commits new list
 *   otherSteps         — [{step_id, label, options:[{option_id,label}]}]
 *                        for the "step:*" attribute dropdown
 *   productFamily      — current flow's family, exposed as a picker
 *                        suggestion when the admin picks
 *                        `product_family` as the attribute
 *   rateCardItems      — cross-card rate items for the target SKU
 *                        dropdown, same shape used elsewhere in this
 *                        page
 */
import { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

// Family suggestions — used as datalist entries when attribute =
// product_family so the admin doesn't have to type from memory.
const FAMILY_SUGGESTIONS = ['vinyl', 'cd', 'dvd', 'bluray'];

// Stable per-rule id so React keys don't collide across quick
// add/remove cycles. Falls back to a random string when
// `crypto.randomUUID` is unavailable (older browsers).
const _uid = () => (
    typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2)
);

export default function ConditionalSkuRulesEditor({
    rules = [],
    onChange,
    otherSteps = [],
    productFamily = 'vinyl',
    rateCardItems = [],
}) {
    // Collapsed by default so the option row stays compact when no
    // rules exist yet. Once the admin adds a rule the section expands
    // automatically.
    const [expanded, setExpanded] = useState(rules.length > 0);

    const commit = (nextRules) => onChange?.(nextRules);

    const addRule = () => {
        const next = [
            ...rules,
            {
                id: _uid(),
                conditions: [{ attribute: '', operator: 'equals', value: '' }],
                sales_sku: '',
                label: '',
            },
        ];
        commit(next);
        setExpanded(true);
    };

    const removeRule = (idx) => {
        const next = rules.filter((_, i) => i !== idx);
        commit(next);
    };

    const patchRule = (idx, patch) => {
        const next = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
        commit(next);
    };

    const patchCondition = (ruleIdx, condIdx, patch) => {
        const rule = rules[ruleIdx];
        const nextConds = rule.conditions.map((c, i) => (
            i === condIdx ? { ...c, ...patch } : c
        ));
        patchRule(ruleIdx, { conditions: nextConds });
    };

    const addCondition = (ruleIdx) => {
        const rule = rules[ruleIdx];
        patchRule(ruleIdx, {
            conditions: [
                ...rule.conditions,
                { attribute: '', operator: 'equals', value: '' },
            ],
        });
    };

    const removeCondition = (ruleIdx, condIdx) => {
        const rule = rules[ruleIdx];
        if (rule.conditions.length <= 1) return; // keep at least one
        patchRule(ruleIdx, {
            conditions: rule.conditions.filter((_, i) => i !== condIdx),
        });
    };

    // Suggest option ids from the referenced step so admins don't
    // have to remember them — powers the datalist on the value input
    // when the attribute is step:<x>.
    const suggestionsFor = (attribute) => {
        if (attribute === 'product_family') return FAMILY_SUGGESTIONS;
        if (attribute?.startsWith('step:')) {
            const stepId = attribute.slice(5);
            const step = otherSteps.find((s) => s.step_id === stepId);
            return (step?.options || []).map((o) => o.option_id);
        }
        return [];
    };

    return (
        <div className="mt-3 border border-dashed border-indigo-200 rounded-lg bg-indigo-50/30 p-3">
            <button
                type="button"
                className="w-full flex items-center justify-between text-left"
                onClick={() => setExpanded((v) => !v)}
                data-testid="toggle-conditional-sku-rules"
            >
                <span className="text-xs font-semibold uppercase tracking-wide text-indigo-700 flex items-center gap-1">
                    {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    Conditional SKU rules
                    {rules.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-medium">
                            {rules.length}
                        </span>
                    )}
                </span>
                <span
                    onClick={(e) => { e.stopPropagation(); addRule(); }}
                    className="text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1 cursor-pointer"
                    data-testid="add-conditional-sku-rule"
                >
                    <Plus className="h-3 w-3" />
                    Add rule
                </span>
            </button>

            {expanded && (
                <>
                    {rules.length === 0 && (
                        <p className="text-xs text-slate-500 italic mt-2">
                            No rules yet — the default Sales SKU above will be used.
                            Add a rule to swap the SKU based on earlier flow selections.
                        </p>
                    )}
                    {rules.map((rule, ruleIdx) => (
                        <div
                            key={rule.id || ruleIdx}
                            className="mt-3 border border-indigo-200 rounded-lg bg-white p-3"
                            data-testid={`conditional-sku-rule-${ruleIdx}`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[11px] uppercase tracking-wide text-indigo-500 font-semibold">
                                    Rule {ruleIdx + 1}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeRule(ruleIdx)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                    title="Delete rule"
                                    data-testid={`delete-conditional-sku-rule-${ruleIdx}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>

                            {/* Conditions */}
                            <div className="space-y-2">
                                {rule.conditions.map((cond, condIdx) => (
                                    <div
                                        key={condIdx}
                                        className="grid grid-cols-12 gap-2 items-end"
                                        data-testid={`rule-${ruleIdx}-condition-${condIdx}`}
                                    >
                                        <div className="col-span-5">
                                            <label className="block text-[10px] uppercase text-slate-400 mb-0.5">
                                                When
                                            </label>
                                            <select
                                                value={cond.attribute || ''}
                                                onChange={(e) => patchCondition(ruleIdx, condIdx, { attribute: e.target.value })}
                                                className="w-full px-2 py-1.5 text-xs border border-border rounded"
                                                data-testid={`rule-${ruleIdx}-condition-${condIdx}-attribute`}
                                            >
                                                <option value="">Choose attribute…</option>
                                                <optgroup label="Earlier flow steps">
                                                    {otherSteps.map((s) => (
                                                        <option key={s.step_id} value={`step:${s.step_id}`}>
                                                            {s.label || s.step_id} (step)
                                                        </option>
                                                    ))}
                                                </optgroup>
                                                <optgroup label="Flow-level">
                                                    <option value="product_family">Product family</option>
                                                </optgroup>
                                                <optgroup label="Custom">
                                                    <option value="__custom__">Attribute key…</option>
                                                </optgroup>
                                            </select>
                                            {cond.attribute === '__custom__' && (
                                                <input
                                                    type="text"
                                                    placeholder="attribute:field_id or literal key"
                                                    value=""
                                                    onChange={(e) => patchCondition(ruleIdx, condIdx, { attribute: e.target.value })}
                                                    className="mt-1 w-full px-2 py-1.5 text-xs border border-border rounded"
                                                />
                                            )}
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-[10px] uppercase text-slate-400 mb-0.5">Op</label>
                                            <select
                                                value={cond.operator || 'equals'}
                                                onChange={(e) => patchCondition(ruleIdx, condIdx, { operator: e.target.value })}
                                                className="w-full px-2 py-1.5 text-xs border border-border rounded"
                                                disabled
                                                title="v1: only equals is supported"
                                            >
                                                <option value="equals">equals</option>
                                            </select>
                                        </div>
                                        <div className="col-span-4">
                                            <label className="block text-[10px] uppercase text-slate-400 mb-0.5">Value</label>
                                            <input
                                                list={`rule-${ruleIdx}-${condIdx}-suggestions`}
                                                type="text"
                                                value={cond.value || ''}
                                                onChange={(e) => patchCondition(ruleIdx, condIdx, { value: e.target.value })}
                                                placeholder="e.g. 7in"
                                                className="w-full px-2 py-1.5 text-xs border border-border rounded"
                                                data-testid={`rule-${ruleIdx}-condition-${condIdx}-value`}
                                            />
                                            <datalist id={`rule-${ruleIdx}-${condIdx}-suggestions`}>
                                                {suggestionsFor(cond.attribute).map((s) => (
                                                    <option key={s} value={s} />
                                                ))}
                                            </datalist>
                                        </div>
                                        <div className="col-span-1 flex justify-end">
                                            {rule.conditions.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeCondition(ruleIdx, condIdx)}
                                                    className="p-1 text-slate-400 hover:text-red-500"
                                                    title="Remove condition"
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addCondition(ruleIdx)}
                                    className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1 mt-1"
                                    data-testid={`rule-${ruleIdx}-add-condition`}
                                >
                                    <Plus className="h-3 w-3" />
                                    Add condition (AND)
                                </button>
                            </div>

                            {/* Target SKU + label */}
                            <div className="grid grid-cols-12 gap-2 mt-3">
                                <div className="col-span-7">
                                    <label className="block text-[10px] uppercase text-slate-400 mb-0.5">
                                        Use Sales SKU
                                    </label>
                                    <select
                                        value={rule.sales_sku || ''}
                                        onChange={(e) => patchRule(ruleIdx, { sales_sku: e.target.value })}
                                        className="w-full px-2 py-1.5 text-xs border border-border rounded"
                                        data-testid={`rule-${ruleIdx}-target-sku`}
                                    >
                                        <option value="">Choose SKU…</option>
                                        {rateCardItems.map((item) => (
                                            <option key={item.sales_sku} value={item.sales_sku}>
                                                {item.sales_sku} — {item.display_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-span-5">
                                    <label className="block text-[10px] uppercase text-slate-400 mb-0.5">
                                        Label (optional hint)
                                    </label>
                                    <input
                                        type="text"
                                        value={rule.label || ''}
                                        onChange={(e) => patchRule(ruleIdx, { label: e.target.value })}
                                        placeholder='e.g. 7" inner sleeve'
                                        className="w-full px-2 py-1.5 text-xs border border-border rounded"
                                        data-testid={`rule-${ruleIdx}-label`}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    {rules.length > 1 && (
                        <p className="text-[11px] text-slate-400 mt-2">
                            Rules are evaluated top-down. The first fully matching rule wins.
                        </p>
                    )}
                </>
            )}

            {/* Product family hint — surfaced separately for admin memory. */}
            {productFamily && (
                <p className="text-[10px] text-slate-400 mt-2">
                    This flow's product family: <span className="font-mono">{productFamily}</span>
                </p>
            )}
        </div>
    );
}
